import React, { useEffect, useState } from 'react'
import { DeckState, generateInitialDeckState } from 'app-redux/deck/initialState'
import { useAppSelector } from 'app-redux/hooks'

import useCardFilterAndSort from 'hooks/useCardFilterAndSort'

import { generateTabState } from 'types/searchV2'
import { SortOptionType, HUMAN_SORT, SORT } from 'types/deck'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import Tab from 'components/cardSearchPanel/tabs/tab'
import LineLoader from 'components/elements/LineLoader'
import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import CardFilters from 'components/filters'
import PhatDropdown from 'components/formElements/PhatDropdown'

import { deckResponseToDeckState } from 'utils/responseToDeck'

import styles from './packageDetailsOverlay.module.scss'

const excludedSortOptions = ['Category quantity']

const sortOptions: { text: string; key: SortOptionType; value: SortOptionType }[] = Object.keys(HUMAN_SORT)
  .filter(text => !excludedSortOptions.includes(text))
  .map(text => ({
    text,
    key: HUMAN_SORT[text],
    value: HUMAN_SORT[text],
  }))

type Props = {
  deckId: number | null
  onClose: () => void
}

const PackageDetailsOverlay = ({ deckId, onClose }: Props) => {
  const [loading, setLoading] = useState(false)

  const [tab, _setTab] = useState(generateTabState.other())
  const [deck, setDeck] = useState<DeckState>()
  const [filteredCards, setFilteredCards] = useState<string[]>([])
  const [orderBy, setOrderBy] = useState(SORT.DATE_ADDED)
  const [sortDirectionDescending, setSortDirectionDescending] = useState(false)

  const packageCards = Object.values(deck?.cardMap || {}).map(card => ({ ...card, qty: 0 }))
  const cards = useCardFilterAndSort(packageCards, orderBy, sortDirectionDescending, filteredCards)

  useEffect(() => {
    if (!deckId) return

    setLoading(true)

    DeckService.details(deckId)
      .then(res => {
        const cleanDeckState = generateInitialDeckState()
        const deck = deckResponseToDeckState(cleanDeckState, res, true)

        setDeck(deck)
      })
      .catch(() => ToastService.create('Failed to package details', 'Card packages'))
      .finally(() => setLoading(false))
  }, [deckId])

  return (
    <GlobalOverlayStack open={!!deckId} onClose={onClose} className={styles.container}>
      {loading && <LineLoader />}

      <div className={styles.controls}>
        <h1>{deck?.name}</h1>

        <div className={styles.filters}>
          <CardFilters cardsToFilter={packageCards} filteredCards={filteredCards} setFilteredCards={setFilteredCards} />
          <PhatDropdown
            header="Sort by"
            icon={sortDirectionDescending ? 'sort amount down' : 'sort amount up'}
            value={orderBy}
            triggerWrapperClassName={styles.dropdowns}
            menuClassName={styles.orderByMenu}
            className={styles.orderByDropdown}
            options={[
              {
                label: 'Order descending',
                onChange: () => setSortDirectionDescending(!sortDirectionDescending),
                type: 'checkbox',
                checked: sortDirectionDescending,
              },
              { type: 'spacer' },
              ...sortOptions.map(opt => ({
                label: opt.text,
                id: `${opt.value}`,
                onClick: () => setOrderBy(opt.value),
              })),
            ]}
          />
        </div>
      </div>

      <Tab tab={tab} initailResults={cards} onTabUpdate={() => null} active />
    </GlobalOverlayStack>
  )
}

export default PackageDetailsOverlay
